import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SmartCategoriesSettings } from "@/components/settings/SmartCategoriesSettings";
import { SmartNotesSettings } from "@/components/settings/SmartNotesSettings";
import { PresetManager } from "@/components/settings/PresetManager";
import { SecuritySettings } from "@/components/settings/SecuritySettings";
import { UserCreditUsage } from "@/components/settings/UserCreditUsage";
import { BillingDetailsSettings } from "@/components/settings/BillingDetailsSettings";
import { ThemeSettings } from "@/components/settings/ThemeSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { LogOut, User, CheckCircle, Coins, CreditCard, Zap, RefreshCw, AlertCircle, Clock, ArrowUpCircle, FileText, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useTestClock } from "@/contexts/TestClockContext";
import { useCreditSystem } from "@/hooks/useCreditSystem";
import { checkAdminAuthSession } from "@/utils/deviceFingerprint";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

const topupPackages = [
  { name: "Mini", credits: 100, price: 5, discount: null },
  { name: "Standard", credits: 500, price: 18, discount: "10% off" },
  { name: "Power", credits: 2000, price: 55, discount: "30% off" },
  { name: "Agency", credits: 10000, price: 220, discount: "45% off" },
];

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { subscription, isLoading: subLoading, refresh: refreshSubscription } = useSubscription();
  const { isTestMode, openCheckoutInNewTab, getCurrentTime } = useTestClock();
  const { stats, credits, isLoading: creditsLoading } = useCreditSystem();
  const { usedLast7Days, totalUsed, usedThisPeriod, currentBalance } = stats;
  const [purchasingPackage, setPurchasingPackage] = useState<string | null>(null);
  const [purchasingDebug, setPurchasingDebug] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdminStatus, setCheckingAdminStatus] = useState(true);

  // Check admin status - only check database role (no auth session check)
  useEffect(() => {
    const checkAdmin = async () => {
      const { checkDatabaseAdminRole } = await import('@/utils/deviceFingerprint');
      const hasAdminRole = await checkDatabaseAdminRole();
      setIsAdmin(hasAdminRole);
      setCheckingAdminStatus(false);
    };
    checkAdmin();
  }, []);

  // Handle session token from Add-in
  useEffect(() => {
    const handleTokenFromUrl = async () => {
      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get('token');
      const refreshToken = params.get('refresh_token');
      
      if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        
        if (!error) {
          window.history.replaceState({}, '', '/settings');
        }
      }
    };
    
    handleTokenFromUrl();
  }, []);

  // Handle payment success/cancelled notifications
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const paymentType = searchParams.get('type');
    const sessionId = searchParams.get('session_id');
    const cycleChanged = searchParams.get('cycle_changed');
    const planChanged = searchParams.get('plan_changed');

    // Handle billing cycle or plan changes
    if (cycleChanged === 'success') {
      toast({
        title: "Billing Cycle Changed!",
        description: "Your subscription billing cycle has been updated successfully.",
      });
      refreshSubscription();
      setSearchParams({});
      return;
    }

    if (planChanged === 'success') {
      toast({
        title: "Plan Changed!",
        description: "Your subscription plan has been updated successfully.",
      });
      refreshSubscription();
      setSearchParams({});
      return;
    }
    
    if (paymentStatus === 'success') {
      if (paymentType === 'topup') {
        // Webhook handles verification automatically
        toast({
          title: "Top-up Processing!",
          description: "Your credits will be added shortly. Refreshing in a few seconds...",
        });
        
        // Delayed refresh to show new credits
        setTimeout(() => {
          refreshSubscription();
        }, 3000);
        
        setSearchParams({});
      } else if (paymentType === 'subscription') {
        // Check subscription status and send confirmation email
        setTimeout(async () => {
          await refreshSubscription();
          
          // Send confirmation email
          try {
            const { data: subData } = await supabase.functions.invoke('check-subscription');
            if (subData?.subscribed && user?.email) {
              await supabase.functions.invoke('send-transactional-email', {
                body: {
                  to: user.email,
                  toName: user.email.split('@')[0],
                  emailType: 'subscription_activated',
                  templateVars: {
                    tier: subData.tier,
                    credits: subData.credits_monthly?.toString(),
                    billingCycle: subData.billing_cycle,
                  },
                },
              });
              console.log('[Settings] Subscription confirmation email sent');
            }
          } catch (emailError) {
            console.error('[Settings] Failed to send confirmation email:', emailError);
          }
        }, 2000);
        
        toast({
          title: "Subscription Activated!",
          description: "Your subscription is now active. Check your email for confirmation.",
        });
      }
      // Clear the query params
      setSearchParams({});
    } else if (paymentStatus === 'cancelled') {
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled. No charges were made.",
        variant: "destructive",
      });
      // Clear the query params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, toast, refreshSubscription]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been logged out",
      });
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Error signing out",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  const handleBuyTopup = async (pkg: typeof topupPackages[0]) => {
    setPurchasingPackage(pkg.name);
    try {
      const { data, error } = await supabase.functions.invoke('create-topup-payment', {
        body: {
          package: pkg.name.toLowerCase(),
        },
      });

      if (error) {
        // Check if it's a subscription error
        if (error.message?.includes('active subscription')) {
          toast({
            title: "Subscription Required",
            description: "Please subscribe to a plan before purchasing top-ups.",
            variant: "destructive",
          });
          // Refresh subscription status in case it changed
          await refreshSubscription();
          setPurchasingPackage(null);
          return;
        }
        throw error;
      }

      // Redirect to Stripe Checkout
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Top-up purchase error:', err);
      toast({
        title: "Purchase Failed",
        description: err instanceof Error ? err.message : "Please try again or contact support.",
        variant: "destructive",
      });
      setPurchasingPackage(null);
    }
  };

  const handleRefreshSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) throw error;

      if (data?.subscribed) {
        toast({
          title: "Subscription Active",
          description: `${data.tier.charAt(0).toUpperCase() + data.tier.slice(1)} plan - ${data.credits_monthly} credits/month`,
        });
      } else {
        toast({
          title: "No Active Subscription",
          description: "Subscribe to a plan to get started.",
        });
      }
      
      await refreshSubscription();
    } catch (err) {
      console.error('Refresh error:', err);
      toast({
        title: "Refresh Failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) throw error;

      // Check for structured error responses
      if (data?.error) {
        const errorCode = data.error;
        const errorMessage = data.message || "Could not open subscription management";

        switch (errorCode) {
          case 'NO_SUBSCRIPTION':
            toast({
              title: "No Subscription Found",
              description: "You need an active subscription to manage billing. Subscribe to a plan to get started.",
              variant: "destructive",
              action: (
                <Button variant="outline" size="sm" onClick={() => navigate('/plans')}>
                  View Plans
                </Button>
              ),
            });
            break;
          
          case 'NO_STRIPE_CUSTOMER':
            toast({
              title: "No Billing Account",
              description: "No billing account found. Please subscribe to a plan first.",
              variant: "destructive",
              action: (
                <Button variant="outline" size="sm" onClick={() => navigate('/plans')}>
                  View Plans
                </Button>
              ),
            });
            break;
          
          case 'MODE_MISMATCH':
            toast({
              title: "Billing Account Error",
              description: errorMessage,
              variant: "destructive",
            });
            break;
          
          default:
            toast({
              title: "Error",
              description: errorMessage,
              variant: "destructive",
            });
        }
        return;
      }

      if (data?.url) {
        if (openCheckoutInNewTab) {
          window.open(data.url, '_blank');
        } else {
          window.location.href = data.url;
        }
        toast({
          title: "Opening Customer Portal",
          description: "You can manage your subscription in the new window",
        });
      }
    } catch (err) {
      console.error('Portal error:', err);
      toast({
        title: "Error",
        description: "Could not open subscription management. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBuyDebugSubscription = async () => {
    setPurchasingDebug(true);
    try {
      console.log('[Settings] Initiating debug subscription checkout');
      
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: {
          tier: 'debug',
          billing_cycle: 'daily',
        },
      });

      if (error) {
        console.error('[Settings] Debug subscription error:', error);
        throw error;
      }

      if (data?.url) {
        console.log('[Settings] Redirecting to Stripe checkout:', data.url);
        if (openCheckoutInNewTab) {
          window.open(data.url, '_blank');
          toast({
            title: "Opening Checkout",
            description: "Debug subscription checkout opened in new tab",
          });
        } else {
          window.location.href = data.url;
        }
      }
    } catch (err) {
      console.error('[Settings] Debug subscription error:', err);
      toast({
        title: "Error",
        description: "Could not create debug subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPurchasingDebug(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50/30 via-white to-blue-50/30 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-blob pointer-events-none" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-blob animation-delay-2000 pointer-events-none" />
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} />
      
      <Navbar />
      <main className="flex-1 py-8 relative z-10">
        <div className="container px-4 max-w-4xl mx-auto">
          {/* Test Mode Banner */}
          {isTestMode && (
            <Alert className="mb-6 border-blue-500 bg-blue-50 dark:bg-blue-950">
              <Clock className="h-4 w-4 text-blue-600" />
              <AlertDescription>
                <strong>🧪 Test Mode Active:</strong> All dates and times shown are simulated using Stripe Test Clock.
                <br />
                <span className="text-xs">
                  Current simulated time: {format(getCurrentTime(), 'PPP HH:mm:ss')}
                </span>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Settings</h1>
            <div className="flex gap-2">
              {checkingAdminStatus ? (
                <div className="h-10 w-32 bg-muted animate-pulse rounded-md" />
              ) : (
                isAdmin && (
                  <Button asChild variant="default" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                    <Link to="/admin" className="gap-2">
                      <Shield className="h-4 w-4" />
                      Admin Dashboard
                    </Link>
                  </Button>
                )
              )}
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Login Status Card */}
            <Card className="glass-card-strong hover-lift transition-all">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 flex items-center justify-center animate-float">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-gradient">Account Status</CardTitle>
                </div>
                <CardDescription>Your current login status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Email</p>
                      <p className="text-sm text-muted-foreground font-mono">
                        {user?.email || 'Not logged in'}
                      </p>
                    </div>
                    <Badge variant="default">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Active
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Subscription</p>
                      <p className="text-sm text-muted-foreground">
                        {subscription ? 'Active subscription' : 'No subscription'}
                      </p>
                    </div>
                    {subscription ? (
                      <Badge variant="default" className="bg-gradient-to-r from-green-600 to-green-500 animate-pulse-glow">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        OK
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="animate-pulse">
                        <AlertCircle className="mr-1 h-3 w-3" />
                        No Subscription
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Add-in Access</p>
                      {user?.email && subscription ? (
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                          <p className="text-xs text-green-700 dark:text-green-300 font-medium">
                            You can use this account in the Outlook Add-in
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <AlertCircle className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                          <p className="text-xs text-orange-700 dark:text-orange-300 font-medium">
                            {!user?.email 
                              ? 'Email verification required for Add-in access'
                              : 'Active subscription required for Add-in access'
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {!checkingAdminStatus && isAdmin && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Admin Access</p>
                        <p className="text-sm text-muted-foreground">
                          You have administrator privileges
                        </p>
                      </div>
                      <Badge variant="default" className="bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse-glow">
                        <Shield className="mr-1 h-3 w-3" />
                        Admin
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Subscription Card */}
            <Card className="glass-card-strong hover-lift transition-all">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 flex items-center justify-center animate-float">
                    <CreditCard className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-gradient">Subscription & Credits</CardTitle>
                </div>
                <CardDescription>Manage your plan and credits</CardDescription>
              </CardHeader>
              <CardContent>
                {subLoading ? (
                  <p className="text-sm text-muted-foreground">Loading subscription...</p>
                ) : subscription ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Current Plan</p>
                      <p className="text-2xl font-bold capitalize">{subscription.tier}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary">
                          {subscription.billing_cycle === 'annual' ? 'Annual' : subscription.billing_cycle === 'daily' ? 'Daily' : 'Monthly'}
                        </Badge>
                        {subscription.tier === 'debug' && (
                          <Badge variant="outline" className="border-orange-500 text-orange-600 bg-orange-50 dark:bg-orange-950">
                            ⚡ Debug Mode
                          </Badge>
                        )}
                        {subscription.status === 'cancelled' && (
                          <Badge variant="destructive">Cancelled</Badge>
                        )}
                        {subscription.cancel_at_period_end && subscription.status === 'active' && (
                          <Badge variant="outline" className="border-orange-500 text-orange-600 dark:text-orange-400">
                            Cancelling Soon
                          </Badge>
                        )}
                      </div>

                      {/* Show renewal and/or scheduled change info for active subscriptions */}
                      {subscription.status === 'active' && !subscription.cancel_at_period_end && (() => {
                        const hasScheduledChange = !!subscription.scheduled_tier;
                        const renewalDate = new Date(subscription.current_period_end);
                        const now = getCurrentTime();
                        const daysUntilRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                        const changeDate = subscription.scheduled_change_date 
                          ? new Date(subscription.scheduled_change_date) 
                          : null;
                        
                        // Fall 1: Kein Wechsel geplant → Nur Renewal zeigen
                        if (!hasScheduledChange) {
                          return (
                            <div className="mt-3 p-2.5 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                              <div className="flex items-start gap-2">
                                <Clock className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                <div className="text-sm">
                                  <p className="text-green-900 dark:text-green-200">
                                    Next renewal:{" "}
                                    <span className="font-semibold">
                                      {daysUntilRenewal > 0 
                                        ? `in ${daysUntilRenewal} ${daysUntilRenewal === 1 ? 'day' : 'days'}` 
                                        : daysUntilRenewal === 0 
                                        ? 'today' 
                                        : `${Math.abs(daysUntilRenewal)} ${Math.abs(daysUntilRenewal) === 1 ? 'day' : 'days'} ago`}
                                    </span>
                                    {isTestMode && (
                                      <span className="text-xs text-blue-600 dark:text-blue-400 ml-1">
                                        ({formatInTimeZone(renewalDate, Intl.DateTimeFormat().resolvedOptions().timeZone, "PPP")})
                                      </span>
                                    )}
                                  </p>
                                   <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                                    Your {subscription.credits_monthly} credits will be automatically renewed.
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Fall 2: Zeige nur Renewal (Wechsel wird in separater "Scheduled Change" Box unten angezeigt)
                        return (
                          <div className="mt-3">
                            {/* Renewal Info */}
                            <div className="p-2.5 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                              <div className="flex items-start gap-2">
                                <Clock className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                <div className="text-sm">
                                  <p className="text-green-900 dark:text-green-200">
                                    Next renewal:{" "}
                                    <span className="font-semibold">
                                      {daysUntilRenewal > 0 
                                        ? `in ${daysUntilRenewal} ${daysUntilRenewal === 1 ? 'day' : 'days'}` 
                                        : daysUntilRenewal === 0 
                                        ? 'today' 
                                        : `${Math.abs(daysUntilRenewal)} ${Math.abs(daysUntilRenewal) === 1 ? 'day' : 'days'} ago`}
                                    </span>
                                  </p>
                                   <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                                    Your {subscription.credits_monthly} credits will be renewed.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Show warning for scheduled cancellation */}
                      {subscription.cancel_at_period_end && subscription.status === 'active' && (
                        <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-md">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400 mt-0.5" />
                            <div className="text-sm space-y-1">
                              <p className="font-medium text-orange-900 dark:text-orange-200">
                                Subscription Scheduled for Cancellation
                              </p>
                              <p className="text-orange-700 dark:text-orange-300">
                                Your subscription will remain active until{" "}
                                <span className="font-semibold">
                                  {formatInTimeZone(
                                    new Date(subscription.current_period_end),
                                    Intl.DateTimeFormat().resolvedOptions().timeZone,
                                    "PPP"
                                  )}
                                </span>
                                .
                              </p>
                              <p className="text-orange-700 dark:text-orange-300 font-medium">
                                ⚠️ All credits ({subscription.credits_remaining}) will expire on this date.
                              </p>
                              <div className="mt-2 pt-2 border-t border-orange-200 dark:border-orange-800">
                                <p className="text-xs text-orange-600 dark:text-orange-400">
                                  You can reactivate your subscription anytime before this date by clicking{" "}
                                  <button 
                                    onClick={handleManageSubscription}
                                    className="underline font-medium hover:text-orange-800 dark:hover:text-orange-300"
                                  >
                                    Manage Subscription
                                  </button>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Top-Up Activation Banner - for active OR cancelled-but-running subscriptions */}
                      {subscription && (subscription.status === 'active' || 
                        (subscription.status === 'cancelled' && subscription.current_period_end && 
                         new Date(subscription.current_period_end) > new Date())) && (
                        <Alert className="mt-4 border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
                          <ArrowUpCircle className="h-5 w-5 text-blue-600" />
                          <AlertDescription>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-blue-900 dark:text-blue-100">
                                  Need More Credits? 🚀
                                </p>
                                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                  Top-up your account now and keep your workflow running smoothly.
                                </p>
                              </div>
                              <Button 
                                variant="default" 
                                size="sm"
                                onClick={() => {
                                  document.getElementById('topup-section')?.scrollIntoView({ 
                                    behavior: 'smooth', 
                                    block: 'center' 
                                  });
                                }}
                                className="ml-4 bg-blue-600 hover:bg-blue-700"
                              >
                                View Top-Ups ↓
                              </Button>
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Show warning for fully cancelled subscription */}
                      {subscription.status === 'cancelled' && !subscription.cancel_at_period_end && (
                        <div className="mt-3 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5" />
                            <div className="text-sm space-y-2">
                              <p className="font-medium text-red-900 dark:text-red-200">
                                Subscription Cancelled
                              </p>
                              <p className="text-red-700 dark:text-red-300">
                                {new Date(subscription.current_period_end) > new Date() ? (
                                  <>
                                    Your subscription will end on{" "}
                                    <span className="font-semibold">
                                      {formatInTimeZone(
                                        new Date(subscription.current_period_end),
                                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                                        "PPP"
                                      )}
                                    </span>
                                    . All credits will expire then.
                                  </>
                                ) : (
                                  <>
                                    Your subscription ended on{" "}
                                    <span className="font-semibold">
                                      {formatInTimeZone(
                                        new Date(subscription.current_period_end),
                                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                                        "PPP"
                                      )}
                                    </span>
                                    . All credits have expired.
                                  </>
                                )}
                              </p>
                              <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800">
                                <p className="text-red-700 dark:text-red-300 mb-2">
                                  {new Date(subscription.current_period_end) > new Date() 
                                    ? "Want to continue? You can reactivate or choose a new plan anytime." 
                                    : "Ready to get back? Choose a plan to continue using premium features."}
                                </p>
                                <Button 
                                  asChild
                                  size="sm"
                                  className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white"
                                >
                                  <Link to="/plans">
                                    <ArrowUpCircle className="mr-2 h-4 w-4" />
                                    Browse Plans
                                  </Link>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Show info for scheduled plan change */}
                      {!subscription.cancel_at_period_end && subscription.status !== 'cancelled' && subscription.scheduled_tier && subscription.scheduled_change_date && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                          <div className="flex items-start gap-2">
                            <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                            <div className="text-sm space-y-1">
                              <p className="font-medium text-blue-900 dark:text-blue-200">
                                Scheduled Change
                              </p>
                              {subscription.scheduled_tier === 'pending' ? (
                                <p className="text-blue-700 dark:text-blue-300">
                                  You have a subscription change scheduled for{" "}
                                  <span className="font-semibold">
                                    {formatInTimeZone(
                                      new Date(subscription.scheduled_change_date),
                                      Intl.DateTimeFormat().resolvedOptions().timeZone,
                                      "PPP"
                                    )}
                                  </span>
                                  . Manage this change in your{" "}
                                  <button 
                                    onClick={handleManageSubscription}
                                    className="underline font-medium hover:text-blue-800 dark:hover:text-blue-300"
                                  >
                                    Customer Portal
                                  </button>.
                                </p>
                              ) : (
                                <p className="text-blue-700 dark:text-blue-300">
                                  Your plan will change to{" "}
                                  <span className="font-semibold capitalize">
                                    {subscription.scheduled_tier}
                                  </span>
                                  {subscription.scheduled_billing_cycle && ` (${subscription.scheduled_billing_cycle})`} on{" "}
                                  <span className="font-semibold">
                                    {formatInTimeZone(
                                      new Date(subscription.scheduled_change_date),
                                      Intl.DateTimeFormat().resolvedOptions().timeZone,
                                      "PPP"
                                    )}
                                  </span>.
                                </p>
                              )}
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                                You can modify this change anytime by clicking{" "}
                                <button
                                  onClick={handleManageSubscription}
                                  className="underline font-medium hover:text-blue-800 dark:hover:text-blue-300"
                                >
                                  Manage Subscription
                                </button>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                    </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-muted-foreground">Credits Remaining</p>
                      <div className="flex items-center gap-1">
                        <Coins className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">
                          {currentBalance ?? subscription.credits_remaining}
                        </span>
                      </div>
                    </div>
                    
                    {/* Combined Credits Bar - Lovable-Style with Tooltips */}
                    <div className="space-y-2">
              {/* Custom Stacked Progress Bar with Tooltips */}
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
                  {(() => {
                    // Berechnungen mit useCreditSystem
                    const monthlyCredits = subscription.credits_monthly;
                    const remainingCredits = currentBalance ?? subscription.credits_remaining;
                    
                    // Remaining Credits aufteilen
                    const remainingMonthlyCredits = Math.min(remainingCredits, monthlyCredits);
                    const remainingAdditionalCredits = Math.max(0, remainingCredits - monthlyCredits);
                    
                    // Used Credits berechnen (einfache Subtraktion!)
                    const usedMonthlyCredits = monthlyCredits - remainingMonthlyCredits;
                    
                    // Total für Prozentberechnung
                    const totalCredits = monthlyCredits + remainingAdditionalCredits;
                    
                    // Prozentsätze
                    const monthlyRemainingPercent = (remainingMonthlyCredits / totalCredits) * 100;
                    const additionalRemainingPercent = (remainingAdditionalCredits / totalCredits) * 100;
                    const usedMonthlyPercent = (usedMonthlyCredits / totalCredits) * 100;
                    
                    return (
                      <>
                        {/* 1. Monthly Remaining (Dark Blue) - Links bei 0% */}
                        {remainingMonthlyCredits > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="absolute inset-y-0 left-0 bg-primary transition-all cursor-pointer hover:opacity-90 pointer-events-auto"
                                style={{ 
                                  width: `${monthlyRemainingPercent}%` 
                                }}
                              />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <div className="text-xs">
                                <p className="font-semibold">Monthly Credits</p>
                                <p className="text-muted-foreground">
                                  {remainingMonthlyCredits.toLocaleString()} of {monthlyCredits.toLocaleString()} remaining
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        
                        {/* 2. Additional Remaining (Light Blue) */}
                        {remainingAdditionalCredits > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="absolute inset-y-0 bg-blue-400 transition-all cursor-pointer hover:opacity-90 pointer-events-auto"
                                style={{ 
                                  left: `${monthlyRemainingPercent}%`,
                                  width: `${additionalRemainingPercent}%`
                                }}
                              />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <div className="text-xs">
                                <p className="font-semibold">Additional Credits</p>
                                <p className="text-muted-foreground">
                                  {remainingAdditionalCredits.toLocaleString()} remaining
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        
                        {/* 3. Used Credits (Grey) - Rechts */}
                        {usedMonthlyCredits > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="absolute inset-y-0 bg-gray-400 transition-all cursor-pointer hover:opacity-90 pointer-events-auto"
                                style={{ 
                                  left: `${monthlyRemainingPercent + additionalRemainingPercent}%`,
                                  width: `${usedMonthlyPercent}%`
                                }}
                              />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <div className="text-xs">
                                <p className="font-semibold">Monthly credits used {usedMonthlyCredits.toLocaleString()}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </>
                    );
                  })()}
                </div>
                      
                      {/* Legend */}
                      <div className="flex items-center gap-4 text-xs flex-wrap">
                        {/* 1. Monthly Credits (Dark Blue) */}
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded-sm bg-primary" />
                          <span className="text-muted-foreground">Monthly credits</span>
                        </div>
                        
                        {/* 2. Intelligent "Using" indicator based on what's being consumed */}
                        {(() => {
                          const currentBalance_local = currentBalance ?? subscription.credits_remaining;
                          const hasAdditionalCredits = currentBalance_local > subscription.credits_monthly;
                          const usedMonthlyCredits = subscription.credits_monthly - Math.min(currentBalance_local, subscription.credits_monthly);
                          
                          // If additional credits exist, show "Using additional credits" (light blue)
                          if (hasAdditionalCredits) {
                            return (
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-sm bg-blue-400" />
                                <span className="text-muted-foreground">Using additional credits</span>
                              </div>
                            );
                          }
                          
                          // If NO additional credits but monthly credits are being used, show "Using monthly credits" (grey)
                          if (usedMonthlyCredits > 0) {
                            return (
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-sm bg-gray-400" />
                                <span className="text-muted-foreground">Using monthly credits</span>
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                      
                      {/* Info text for Additional Credits */}
                      {subscription.credits_remaining > subscription.credits_monthly && (
                        <div className="flex items-start gap-1.5 mt-2 p-2 bg-muted/50 rounded-md">
                          <AlertCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            These additional credits will reset at your next renewal{" "}
                            {(() => {
                              const renewalDate = new Date(subscription.current_period_end);
                              const now = getCurrentTime();
                              const daysUntilRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                              return (
                                <span className="font-medium text-foreground">
                                  {daysUntilRenewal > 0 
                                    ? `in ${daysUntilRenewal} ${daysUntilRenewal === 1 ? 'day' : 'days'}` 
                                    : daysUntilRenewal === 0 
                                    ? 'today' 
                                    : `${Math.abs(daysUntilRenewal)} ${Math.abs(daysUntilRenewal) === 1 ? 'day' : 'days'} ago`}
                                  {isTestMode && (
                                    <span className="text-xs text-blue-600 dark:text-blue-400 ml-1">
                                      ({formatInTimeZone(renewalDate, Intl.DateTimeFormat().resolvedOptions().timeZone, "PPP 'at' p zzz")})
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* 7-Tage-Verbrauch */}
                    <div className="pt-3 border-t">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">Used in last 7 days</p>
                        <span className="text-sm font-medium text-muted-foreground">
                          {Math.round(usedLast7Days)}
                        </span>
                      </div>
                    </div>
                  </div>
                    <div className="pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleManageSubscription}
                        className="w-full"
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        Manage Subscription
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      You don't have an active subscription yet.
                    </p>
                    <Button asChild>
                      <Link to="/plans">Browse Plans</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="subscription" className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="categories">Smart Categories</TabsTrigger>
              <TabsTrigger value="notes">Smart Notes</TabsTrigger>
              <TabsTrigger value="presets">Field Presets</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="appearance">Appearance</TabsTrigger>
            </TabsList>
            <TabsContent value="subscription" className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Subscription Details</CardTitle>
                      <CardDescription>
                        View your current plan and usage
                      </CardDescription>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <FileText className="mr-2 h-4 w-4" />
                          Billing Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Billing Details</DialogTitle>
                          <DialogDescription>
                            Manage your billing information
                          </DialogDescription>
                        </DialogHeader>
                        <BillingDetailsSettings />
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {subscription ? (
                    <div className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium mb-3">Plan Information</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Plan:</span>
                              <span className="font-medium capitalize">{subscription.tier}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Billing:</span>
                              <span className="font-medium capitalize">{subscription.billing_cycle}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Status:</span>
                              <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                                {subscription.status}
                              </Badge>
                            </div>

                            {/* Show renewal date for active subscriptions */}
                            {subscription.status === 'active' && (
                              <div className="flex justify-between pt-2 border-t mt-2">
                                <span className="text-muted-foreground">Next Renewal:</span>
                                <span className="font-medium text-sm">
                                  {(() => {
                                    const renewalDate = new Date(subscription.current_period_end);
                                    const now = getCurrentTime();
                                    const daysUntilRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                    return daysUntilRenewal > 0 
                                      ? `in ${daysUntilRenewal} ${daysUntilRenewal === 1 ? 'day' : 'days'}` 
                                      : daysUntilRenewal === 0 
                                      ? 'today' 
                                      : `${Math.abs(daysUntilRenewal)} ${Math.abs(daysUntilRenewal) === 1 ? 'day' : 'days'} ago`;
                                  })()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium mb-3">Credit Usage</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Monthly Allowance:</span>
                              <span className="font-medium">{subscription.credits_monthly} credits</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Additional:</span>
                              <span className="font-medium">
                                {Math.max(0, (currentBalance ?? subscription.credits_remaining) - subscription.credits_monthly)} credits
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Remaining:</span>
                              <span className="font-medium">{currentBalance ?? subscription.credits_remaining} credits</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Used:</span>
                              <span className="font-medium">
                                {Math.round(totalUsed)} credits
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      
                      {/* Credit Top-Ups Section */}
                      {subscription && (subscription.status === 'active' || 
                        (subscription.status === 'cancelled' && subscription.current_period_end && 
                         new Date(subscription.current_period_end) > new Date())) && (
                        <div className="pt-4 border-t" id="topup-section">
                          <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                            <Zap className="h-4 w-4 text-primary" />
                            Buy Credit Top-Ups
                          </h4>
                          <p className="text-sm text-muted-foreground mb-4">
                            Top-up credits are added to your account and reset to your monthly allocation at the next renewal.
                          </p>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {topupPackages.map((pkg) => (
                              <Card 
                                key={pkg.name} 
                                className={`border-2 flex flex-col transition-all hover:shadow-lg hover:-translate-y-1 ${
                                  pkg.name === 'Power' 
                                    ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950 dark:to-blue-950' 
                                    : 'border-muted'
                                }`}
                              >
                                <CardHeader className="text-center pb-3">
                                  {pkg.name === 'Power' && (
                                    <Badge className="mb-2 mx-auto bg-purple-600">
                                      🏆 BEST VALUE
                                    </Badge>
                                  )}
                                  {pkg.name === 'Standard' && (
                                    <Badge className="mb-2 mx-auto bg-blue-600">
                                      ⭐ POPULAR
                                    </Badge>
                                  )}
                                  {pkg.discount && (
                                    <Badge className="mb-2 mx-auto" variant="secondary">
                                      {pkg.discount}
                                    </Badge>
                                  )}
                                  <CardTitle className="text-base">{pkg.name}</CardTitle>
                                  <div className="mt-2">
                                    <span className="text-xl font-bold">€{pkg.price}</span>
                                  </div>
                                  <div className="flex items-center justify-center gap-1 mt-1">
                                    <Coins className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">
                                      {pkg.credits.toLocaleString()} credits
                                    </span>
                                  </div>
                                </CardHeader>
                                <CardContent className="pt-0 mt-auto">
                                  <Button 
                                    className="w-full" 
                                    size="sm"
                                    onClick={() => handleBuyTopup(pkg)}
                                    disabled={purchasingPackage !== null}
                                  >
                                    {purchasingPackage === pkg.name ? "Processing..." : "Buy Now"}
                                  </Button>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}


                      <div className="pt-4 border-t">
                        <p className="text-sm text-muted-foreground mb-4">
                          Need to change your plan or billing cycle?
                        </p>
                        <div className="flex gap-3">
                          <Button asChild>
                            <Link to="/pricing">View Pricing</Link>
                          </Button>
                          <Button variant="outline" asChild>
                            <Link to="/support">Contact Support</Link>
                          </Button>
                        </div>
                      </div>

                      {/* User Credit Usage History */}
                      <div className="pt-4 border-t">
                        <UserCreditUsage />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="text-center py-8">
                        <Coins className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <h4 className="text-lg font-semibold mb-2">No Active Subscription</h4>
                        <p className="text-muted-foreground mb-4">
                          Start using Email2Contact by choosing a plan that fits your needs.
                        </p>
                        <Button asChild>
                          <Link to="/plans">Browse Plans</Link>
                        </Button>
                      </div>

                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="categories" className="mt-6">
              <SmartCategoriesSettings />
            </TabsContent>
            <TabsContent value="notes" className="mt-6">
              <SmartNotesSettings />
            </TabsContent>
            <TabsContent value="presets" className="mt-6">
              <PresetManager />
            </TabsContent>
            <TabsContent value="security" className="mt-6">
              <SecuritySettings />
            </TabsContent>
            <TabsContent value="appearance" className="mt-6">
              <ThemeSettings />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Settings;
